import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import EventCard from "@/components/EventCard";
import { api } from "@/lib/api";
import { Calendar, Music, Mic, Wrench, Palette, Building2, Filter, Plus, Sparkles, Search } from "lucide-react";

/**
 * /events — dedicated Events & Experiences page.
 *
 * Iter 40 brief:
 *   - Contains ALL latest events AND experiences (no home-page redirect).
 *   - Prominent "List / Host an Event" CTA for organisers and venues.
 *   - Category chips, search, and Upcoming/Past toggle.
 */

const CATEGORY_CHIPS = [
  { key: "all",       label: "All",         Icon: Sparkles,  color: "lime" },
  { key: "music",     label: "Music",       Icon: Music,     color: "magenta" },
  { key: "comedy",    label: "Comedy",      Icon: Mic,       color: "orangex" },
  { key: "experience",label: "Experiences", Icon: Palette,   color: "skyx" },
  { key: "college",   label: "College Fests", Icon: Building2, color: "pink" },
  { key: "workshop",  label: "Workshops",   Icon: Wrench,    color: "lime" },
];

const isPast = (e) => {
  if (!e?.date) return false;
  try {
    const d = e.end_date || e.date;
    const dt = new Date(d.includes("T") ? d : `${d}T23:59:59`);
    return dt < new Date();
  } catch { return false; }
};

const matchesCategory = (e, key) => {
  if (key === "all") return true;
  const bag = `${e.category || ""} ${e.type || ""} ${(e.tags || []).join(" ")}`.toLowerCase();
  if (key === "experience") return (e.type || "").toLowerCase() === "experience" || bag.includes("experience");
  if (key === "college") return bag.includes("college") || bag.includes("fest");
  return bag.includes(key);
};

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    document.title = "All Events & Experiences — Eventom";
    api.get("/events").then(({ data }) => setEvents(data || [])).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return (events || [])
      .filter((e) => e.banner) // require a poster to look premium
      .filter((e) => showPast ? isPast(e) : !isPast(e))
      .filter((e) => matchesCategory(e, cat))
      .filter((e) => !query || (e.title || "").toLowerCase().includes(query) || (e.city || "").toLowerCase().includes(query))
      .sort((a, b) => showPast ? new Date(b.date) - new Date(a.date) : new Date(a.date) - new Date(b.date));
  }, [events, cat, q, showPast]);

  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10 hero-editorial" data-testid="events-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16 relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="chip !text-lime !border-lime/40 mb-4"><Calendar className="w-3 h-3" /> Events &amp; Experiences</div>
              <h1 className="font-fancy text-5xl md:text-7xl leading-[0.92] text-white">
                Every drop. <br/><span className="marker-pink">One page.</span>
              </h1>
              <p className="mt-4 text-muted max-w-lg">Latest events. Live experiences. Book in one tap.</p>
            </div>
            <Link to="/organizer/signup" data-testid="events-host-cta" className="group relative inline-flex items-center gap-3 rounded-3xl border border-lime/50 bg-gradient-to-br from-lime/25 to-lime/5 p-4 hover:border-lime transition-colors shrink-0 max-w-sm">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-lime text-ink shrink-0"><Plus className="w-5 h-5" /></span>
              <div>
                <div className="font-heading font-bold text-white leading-tight">List / Host an Event</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-lime mt-1">Organisers · Venues →</div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* THEMED STICKER MARQUEE — PDF magazine strip between hero + filters */}
      <section className="themed-pink sticker-wall py-2.5 overflow-hidden" data-testid="events-themed-strip">
        <div className="marquee-container">
          <div className="marquee-track font-fancy text-xl md:text-2xl text-white uppercase tracking-tight">
            {Array(3).fill("● TONIGHT · ● TOMORROW · ● THIS WEEKEND · ● 48 HOURS DROPS · ● GIRLS TAKEOVER · ● FOUNDING MEMBERS · ").map((t, i) => (
              <span key={i} className="whitespace-nowrap">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="sticky top-16 z-30 border-b border-white/10 nav-glass py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              data-testid="events-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search events, city…"
              className="input-dark !py-2.5 !pl-9 !text-sm"
            />
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-testid="events-cat-strip">
            {CATEGORY_CHIPS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                data-testid={`events-cat-${key}`}
                onClick={() => setCat(key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-heading font-bold uppercase tracking-widest border transition-all whitespace-nowrap ${
                  cat === key ? "bg-lime text-ink border-lime" : "bg-white/5 border-white/10 text-white/85 hover:border-white/25"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
          <div className="ml-auto inline-flex rounded-full border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="events-time-toggle">
            {[
              { k: false, label: "Upcoming" },
              { k: true,  label: "Past" },
            ].map(({ k, label }) => (
              <button
                key={String(k)}
                onClick={() => setShowPast(k)}
                data-testid={`events-time-${k ? "past" : "upcoming"}`}
                className={`px-3 py-1.5 text-[11px] font-heading font-bold uppercase tracking-widest transition-colors ${showPast === k ? "bg-white text-ink" : "text-white/70 hover:text-white"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="events-grid">
        {loading ? (
          <div className="text-center text-muted font-mono text-sm py-16">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="glass rounded-3xl p-10 text-center max-w-md mx-auto" data-testid="events-empty">
            <Filter className="w-8 h-8 text-lime mx-auto mb-3" />
            <div className="font-display text-2xl uppercase text-white">Nothing matches.</div>
            <div className="text-[12px] text-muted mt-2">Try another category or clear the search.</div>
          </div>
        ) : (
          <>
            <div className="mb-5 flex items-baseline justify-between">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted">Showing {filtered.length} {showPast ? "past" : "upcoming"} · {CATEGORY_CHIPS.find((c) => c.key === cat)?.label}</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((e, i) => <EventCard key={e.id} event={e} index={i} isPast={isPast(e)} />)}
            </div>
          </>
        )}
      </section>
    </Layout>
  );
}
